import { FC, useEffect } from 'react';
import { useState } from 'react';
import PropTypes, { element } from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Divider,
  FormGroup,
  Checkbox,
  Switch,
  Box,
  Chip,
  Avatar
} from '@material-ui/core';
import { useDispatch, useSelector } from "react-redux";
import type { Customer } from '../../types/customer';
import { InputField } from '../input-field';
import { AutocompleteField } from '../autocomplete-field';
import { customerPostOne, customerPutOne, customerDeleteOne, customerGetAll } from '../../api/review-1/review-1-customer';
import { XCircle as XCircleIcon } from '../../icons/x-circle';
import { ConfirmationDialog }  from '../confirmation-dialog';
import { filterActiveConcernings, getConcerningDisplayName } from '../../utils/concernings';
import DocumentConcerningsPanel from './document-concernings-panel';
import { companyGetAll } from '../../api/review-1/review-1-company';
import { documentPost, resetDocumentStatus, removeSelectedDocumentConcerning } from '../../slices/documentSlice';
import { RootState } from '../../store';
import { useTheme } from "@material-ui/core/styles";
import { useLocation } from 'react-router-dom';
import { getActiveElement } from '../../utils/active-element';
import { ImageDropzone } from '../image-dropzone';
import filesize from 'filesize';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { lightSuccess } from '../../colors';

// Private = pink, shared = green — same values as the comment dialog and the
// Lists comment filters.
const PRIVACY_PINK = '#E91E63';

interface DocumentDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DocumentDialog: FC<DocumentDialogProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, ...other } = props;
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const { documentIsLoading, documentStatus, documentNextAction, selectedConcernings } = useSelector((state: RootState) => state.document);
  const [showConcerningsPanel, setShowConcerningsPanel] = useState(false);
  // Client/company concernings carry an id in their key, so the chips need the
  // loaded lists to show a name instead of a raw `clients_<uuid>` — same as the
  // Unknown/Factor/Move dialogs.
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const dispatch = useDispatch();

  const location = useLocation();
  const activeElement = getActiveElement(location.pathname);

  // Only once the dialog is actually opened — this component stays mounted in
  // the dashboard shell, so fetching on mount would cost every page load two
  // requests the user may never need.
  useEffect(() => {
    if (!open) {
      return;
    }
    const loadClientsAndCompanies = async () => {
      try {
        const [clientsData, companiesData] = await Promise.all([customerGetAll(), companyGetAll()]);
        if (clientsData !== 'error') {
          setClients(clientsData || []);
        }
        if (companiesData && !companiesData.err) {
          setCompanies(companiesData.items || []);
        }
      } catch (error) {
        console.error('Error loading clients and companies:', error);
      }
    };
    loadClientsAndCompanies();
  }, [open]);

  useEffect(() => {
    console.log('store status', documentIsLoading);
    if (documentStatus === 'success') {
      toast.success(t('upload_created'));
      formik.resetForm();
      formik.setStatus({ success: true });
      formik.setSubmitting(false);
      setFile(null);
      setFilePreview(null);
      dispatch(resetDocumentStatus());
      if (documentNextAction === 'close') {
        onClose();
      }
    } else {
      formik.setStatus({ success: false });
      formik.setErrors({ submit: documentStatus });
      formik.setSubmitting(false);
    }
  }, [documentStatus]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: '',
      isShared: true,
      title: '',
      url: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().min(3).max(255).required('Title is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        // dispatch(review1PostCustomer(values));
      } catch (err) {
        console.error(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    }
  });

  const handleDrop = (newFiles: any): void => {
    console.log(newFiles);
    setFile(newFiles[0]);
    setFilePreview(URL.createObjectURL(newFiles[0]));
  };

  const handleDeleteFile = () => {
    setFile(null);
    setFilePreview(null);
  }

  const handleIsShared = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('isShared', event.target.checked);
  };

  const handleSubmitAndQuit = () => {
    // Auto-prefix the protocol so a pasted "example.com" stays a valid href.
    let url = (formik.values.url || '').trim();
    if (url && !/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    const fileMeta = file ? { size: file.size, type: file.type } : {};
    // Same persisted shape as unknowns.concernings: [{id, value}] as JSON, sent
    // as one multipart field.
    const concerningsPayload = JSON.stringify(
      selectedConcernings.map((concerning) => ({ id: concerning, value: concerning }))
    );
    dispatch(documentPost('close', {
      ...formik.values,
      url,
      ...activeElement,
      ...fileMeta,
      concernings: concerningsPayload
    }, file));
  };

  // A document needs a source: either an uploaded file or a URL.
  const hasSource = Boolean(file) || Boolean((formik.values.url || '').trim());

  // Concernings pointing at a since-deleted client/company are dropped, so the
  // count and the chip row agree.
  const activeConcernings = filterActiveConcernings(selectedConcernings, clients, companies);

  return (
    <Dialog
      // maxWidth="md"
      fullWidth
      onClose={() => {setOpenConfirmationDialog(true)}}
      open={open}
      TransitionProps={{
        onExited: () => formik.resetForm()
      }}
      {...other}
    >
      <DialogTitle sx={{ backgroundColor: "background.default" }}>
        {t('upload_file_or_url')}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "background.default" }}>
        <Grid
          container
          spacing={2}
          my={2}
        >
          <Grid
            item
            xs={12}
          >
            { !file ? (
              <ImageDropzone
                onDrop={handleDrop}
                sx={{ height: '100%' }}
                // accept="image/jpeg, image/png"
              />
            ) :(
              <Grid container sx={{ backgroundColor: 'background.default', borderRadius: 1, padding: 1}} alignItems="center">
                <Grid item xs={2} sx={{ backgroundColor: 'background.paper', borderRadius: 1, padding: 1 }}>
                  {file.type.includes('image/') ? (
                    <img
                      alt=""
                      src={filePreview}
                      style={{ maxWidth: '100%' }}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faFile as IconProp}
                      size="6x"
                      style={{ width: '100%'}} />
                  )}
                </Grid>
                <Grid item xs={1} />
                <Grid item xs={8} >
                  <Typography
                    variant='body1'
                  >
                   {file.name}
                  </Typography>
                  <Typography
                    variant='body2'
                  >
                   {`${file.type} - ${filesize(file.size)}`}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    color="primary"
                    onClick={() => handleDeleteFile()}
                  >
                    <XCircleIcon />
                  </IconButton>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.url && formik.errors.url)}
              fullWidth
              helperText={(formik.touched.url && formik.errors.url) || t('url_helper_text')}
              label={t('url')}
              name="url"
              placeholder="https://example.com"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.url}
              disabled={Boolean(file)}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <InputField
              autoFocus
              error={Boolean(formik.touched.title && formik.errors.title)}
              fullWidth
              helperText={formik.touched.title && formik.errors.title}
              label={t('name_of_upload')}
              name="title"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              required
              value={formik.values.title}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.description && formik.errors.description)}
              fullWidth
              helperText={formik.touched.description && formik.errors.description}
              label={t('description_of_upload')}
              name="description"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.description}
              multiline
              rows={2}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/* Same left-label / switch / right-label shape and the same pink
                (private) vs green (shared) track as the comment dialog, so the
                two privacy toggles read identically. */}
            <FormGroup>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: formik.values.isShared ? 'text.secondary' : 'text.primary' }}>
                  {t('upload_private_label')}
                </Typography>
                <Switch
                  checked={formik.values.isShared}
                  onChange={handleIsShared}
                  sx={{
                    '& .MuiSwitch-track': { backgroundColor: PRIVACY_PINK },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: lightSuccess.main,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ color: formik.values.isShared ? 'text.primary' : 'text.secondary' }}>
                  {t('upload_shared_label')}
                </Typography>
              </Box>
            </FormGroup>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Button
              color="primary"
              size="small"
              variant="outlined"
              onClick={() => setShowConcerningsPanel((shown) => !shown)}
            >
              {t('concerning')} ({activeConcernings.length})
            </Button>
            {activeConcernings.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {activeConcernings.map((concerning, index) => (
                  <Chip
                    key={index}
                    label={getConcerningDisplayName(concerning, clients, companies, t)}
                    size="small"
                    variant="outlined"
                    onDelete={() => dispatch(removeSelectedDocumentConcerning(concerning))}
                  />
                ))}
              </Box>
            )}
            {showConcerningsPanel && (
              <Box sx={{ mt: 1 }}>
                <DocumentConcerningsPanel height={360} onHide={() => setShowConcerningsPanel(false)} />
              </Box>
            )}
          </Grid>
          {formik.errors.submit && (
            <Grid
              item
              xs={12}
            >
              <FormHelperText error>
                {formik.errors.submit}
              </FormHelperText>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ backgroundColor: "background.default" }}>
        <Button
          color="primary"
          size="small"
          onClick={onClose}
          variant="outlined"
          fullWidth
        >
          {t('cancel')}
        </Button>
        <Button
          color="primary"
          size="small"
          // disabled={documentIsLoading}
          disabled={!(formik.isValid && formik.dirty) || !hasSource || documentIsLoading}
          onClick={() => { handleSubmitAndQuit(); }}
          variant="contained"
          fullWidth
        >
          {t('create')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        message={t('do_you_want_to_close_without_saving')}
        onCancel={() => {setOpenConfirmationDialog(false)}}
        onConfirm={() => {
          setOpenConfirmationDialog(false);
          onClose();
        }}
        open={openConfirmationDialog}
        title={t('confirmation_title')}
        variant='warning'
      />
    </Dialog>
  );
};

DocumentDialog.defaultProps = {
  open: false
};

DocumentDialog.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func
};
